import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Activity } from "lucide-react";

/**
 * SystemPulseBar — a very slow-shimmering aurora ribbon that sits above the
 * Command Center header and surfaces live workspace health as a single
 * horizontal readout.
 *
 * Design intent (matches the OS-Control-Center metaphor):
 * - Feels like a heartbeat monitor for your entire event portfolio.
 * - When everything's calm, a positive "ALL SYSTEMS NOMINAL" reads out.
 * - When something needs attention (overdue, unconfirmed, unpaid), those
 *   counters take precedence and the pulse tempo picks up subtly.
 */
export default function SystemPulseBar({ portfolio, lastRefresh, dense = false }) {
  const [, forceTick] = useState(0);
  // Keeps the "REFRESHED Xs ago" chip live without needing the whole dashboard
  // to re-render. Cheap 30s interval.
  useEffect(() => {
    const t = setInterval(() => forceTick((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  const {
    today_count = 0,
    events_this_week = 0,
    overdue_tasks = 0,
    vendors_unconfirmed = 0,
    deposits_unpaid = 0,
    upcoming = 0,
  } = portfolio || {};

  const attentionCount = overdue_tasks + vendors_unconfirmed + deposits_unpaid;
  const status = attentionCount === 0 ? "nominal" : attentionCount <= 3 ? "advisory" : "attention";

  // Data readouts — hide zero-count segments to keep the ribbon clean when calm.
  // Each entry deep-links into the Events workspace pre-filtered to that slice.
  const readouts = [];
  if (today_count > 0)         readouts.push({ label: "TODAY",       value: today_count,         kind: "hot",  to: "/events?filter=today" });
  if (events_this_week > 0)    readouts.push({ label: "THIS WEEK",   value: events_this_week,    kind: "calm", to: "/events?filter=this-week" });
  if (upcoming > 0)            readouts.push({ label: "UPCOMING",    value: upcoming,            kind: "calm", to: "/events?filter=upcoming" });
  if (overdue_tasks > 0)       readouts.push({ label: "OVERDUE",     value: overdue_tasks,       kind: "warn", to: "/events?filter=overdue" });
  if (vendors_unconfirmed > 0) readouts.push({ label: "UNCONFIRMED", value: vendors_unconfirmed, kind: "warn", to: "/events?filter=unconfirmed" });
  if (deposits_unpaid > 0)     readouts.push({ label: "UNPAID",      value: deposits_unpaid,     kind: "warn", to: "/events?filter=unpaid" });

  const refreshedAgo = lastRefresh ? formatRefreshedAgo(lastRefresh) : null;

  return (
    <section
      data-testid="system-pulse-bar"
      aria-label="System status"
      className={`relative overflow-hidden bevel border-hair mb-4 ${dense ? "" : ""}`}
    >
      {/* Aurora shimmer — very slow (14s), low opacity so text stays readable */}
      <div
        className="absolute inset-0 bg-aurora aurora-shimmer opacity-[0.14] pointer-events-none"
        aria-hidden
      />
      {/* Sharp brutalist top rule — 1px inner aurora line signalling the "live wire" */}
      <div
        className="absolute inset-x-0 top-0 h-[1px] bg-aurora opacity-70 pointer-events-none"
        aria-hidden
      />

      <div className="relative flex items-center flex-wrap gap-x-6 gap-y-2 px-4 py-2.5">
        {/* Left: heartbeat + status word */}
        <div className="flex items-center gap-2.5 flex-shrink-0">
          <StatusDot status={status} />
          <Activity size={12} strokeWidth={2} className="text-app" />
          <span
            className="font-mono text-[10px] tracking-[0.22em] uppercase text-app"
            data-testid="system-pulse-status"
          >
            {status === "nominal" && "All Systems Nominal"}
            {status === "advisory" && "Advisory"}
            {status === "attention" && "Needs Attention"}
          </span>
        </div>

        {/* Divider */}
        <span className="font-mono text-[10px] text-faint hidden md:inline" aria-hidden>|</span>

        {/* Middle: rolling readouts */}
        <div className="flex items-center gap-4 flex-wrap flex-1 min-w-0" data-testid="system-pulse-readouts">
          {readouts.length === 0 ? (
            <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-muted">
              No active events · Command Center standing by
            </span>
          ) : (
            readouts.map((r) => (
              <ReadoutChip key={r.label} label={r.label} value={r.value} kind={r.kind} to={r.to} />
            ))
          )}
        </div>

        {/* Right: refresh timestamp */}
        {refreshedAgo && (
          <div
            className="font-mono text-[10px] tracking-[0.18em] uppercase text-faint flex-shrink-0"
            title={`Last refresh: ${new Date(lastRefresh).toLocaleTimeString()}`}
            data-testid="system-pulse-refreshed"
          >
            SYNC · {refreshedAgo}
          </div>
        )}
      </div>
    </section>
  );
}

/* --- helpers --- */

function StatusDot({ status }) {
  const color =
    status === "nominal" ? "#10B981" :
    status === "advisory" ? "#F59E0B" : "#EF4444";
  return (
    <span
      className="relative inline-flex items-center justify-center flex-shrink-0"
      aria-hidden
    >
      <span
        className="absolute w-3 h-3 rounded-full live-pulse"
        style={{ background: color, opacity: 0.35 }}
      />
      <span
        className="relative w-1.5 h-1.5 rounded-full"
        style={{ background: color, boxShadow: `0 0 8px ${color}` }}
      />
    </span>
  );
}

function ReadoutChip({ label, value, kind, to }) {
  // Value color follows severity; label stays monospace-faint so scanning stays quick.
  const valueColor =
    kind === "warn" ? "text-[#F59E0B]" :
    kind === "hot"  ? "text-[#22D3EE]" :
                      "text-app";
  const inner = (
    <>
      <span className={`font-display text-lg font-black tabular-nums leading-none ${valueColor}`}>
        {String(value).padStart(2, "0")}
      </span>
      <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-faint group-hover:text-app transition-colors">
        {label}
      </span>
    </>
  );
  const testId = `system-pulse-chip-${label.toLowerCase().replace(/\s+/g, "-")}`;
  if (to) {
    return (
      <Link
        to={to}
        data-testid={testId}
        aria-label={`Jump to ${label.toLowerCase()} — ${value} item${value === 1 ? "" : "s"}`}
        className="group flex items-baseline gap-1.5 hover:opacity-100 opacity-95 cursor-pointer"
      >
        {inner}
      </Link>
    );
  }
  return (
    <div className="flex items-baseline gap-1.5" data-testid={testId}>
      {inner}
    </div>
  );
}

function formatRefreshedAgo(ts) {
  const then = typeof ts === "number" ? ts : new Date(ts).getTime();
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 5) return "just now";
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  return `${Math.floor(secs / 3600)}h ago`;
}
